// "Which data is this?" - shown when the file doesn't clearly identify its target
// model, and reachable at any time from the wizard so a confident-but-wrong guess can
// be corrected.
//
// Each option shows how many of that model's fields the file's headers actually
// matched, so the choice is evidence-based rather than a bare list of nouns.
import { Alert, Box, Button, Card, CardActionArea, Chip, Typography } from '@mui/material';
import type { ImportModelDetection } from './DetectImportModel';
import { getImportModel } from './ImportModelRegistry';

export interface ModelPickerProps {
    fileName: string;
    detection: ImportModelDetection;
    onSelect: (modelKey: string) => void;
}

export default function ModelPicker(props: ModelPickerProps) {
    const { fileName, detection, onSelect } = props;
    const candidates = detection.ranked.filter(entry => entry.score > 0);
    const shown = candidates.length > 0 ? candidates : detection.ranked;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle1">
                What kind of data is <strong>{fileName}</strong>?
            </Typography>

            {candidates.length === 0 && (
                <Alert severity="warning" variant="outlined">
                    None of the columns in this file look like a model we can import.
                    Pick the closest match and correct the mapping on the next step.
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {shown.map(entry => {
                    const model = getImportModel(entry.model.key) ?? entry.model;
                    const missing = entry.missingRequiredFieldKeys.length;

                    return (
                        <Card key={model.key} variant="outlined" sx={{ minWidth: 220, flex: '1 1 220px' }}>
                            <CardActionArea onClick={() => onSelect(model.key)} sx={{ p: 2, height: '100%' }}>
                                <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                                    {model.plural}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                        size="small"
                                        color={entry.matchedFieldCount > 0 ? 'primary' : 'default'}
                                        variant="outlined"
                                        label={`${entry.matchedFieldCount} of ${entry.totalFieldCount} fields matched`}
                                    />
                                    {missing > 0 && (
                                        <Chip
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                            label={`${missing} required missing`}
                                        />
                                    )}
                                </Box>
                            </CardActionArea>
                        </Card>
                    );
                })}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" disabled>
                    Pick one to continue
                </Button>
            </Box>
        </Box>
    );
}
